import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  IconButton,
  Tooltip,
  Collapse,
  Radio,
  RadioGroup,
  FormLabel,
  FormHelperText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper as TablePaper,
  TablePagination,
  Tab,
  Tabs,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  LocalOffer as LocalOfferIcon,
  Inventory as InventoryIcon,
  AttachMoney as AttachMoneyIcon,
  Info as InfoIcon,
  Business as SupplierIcon,
  LocationOn as LocationIcon,
  Security as WarrantyIcon,
  Calculate as CalculateIcon,
  QrCode as BarcodeIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Check as CheckIcon,
  Search as SearchIcon,
  AddShoppingCart as AddToExistingIcon,  // This was missing
  PostAdd as NewProductIcon,              // This was missing
  Warning as WarningIcon,
  ReportProblem as IssueIcon,
  ConfirmationNumber as SerialIcon,
  CheckCircle as GoodIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlaylistAdd as BulkAddIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Keyboard as KeyboardIcon,
  Memory as MemoryIcon,
  BatteryChargingFull as BatteryIcon,
  Wifi as WifiIcon,
  Speaker as SpeakerIcon,
  Whatshot as WhatshotIcon,
  BugReport as BugReportIcon,
  Build as BuildIcon,
  Category as CategoryIcon,
  FilterAlt as FilterAltIcon,
  Assignment as AssignmentIcon,
  Pending as PendingIcon,
  Update as UpdateIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../../services/api';

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  // Mode selection - 'new' for new product, 'existing' for adding to existing
  const [mode, setMode] = useState('new');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [showAllLocations, setShowAllLocations] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Tab for serial number management
  const [serialTab, setSerialTab] = useState(0);
  
  // Toggle for serial number tracking
  const [trackSerials, setTrackSerials] = useState(false);
  const [showSerialWarning, setShowSerialWarning] = useState(false);

  // For searching existing products
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedExistingProduct, setSelectedExistingProduct] = useState(null);
  const [additionalQuantity, setAdditionalQuantity] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Pagination for search results
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // ==================== SERIAL NUMBERS STATE ====================
  const [goodSerials, setGoodSerials] = useState([]);
  const [issueSerials, setIssueSerials] = useState([]);
  const [newSerial, setNewSerial] = useState('');
  const [editingSerial, setEditingSerial] = useState(null);
  const [serialType, setSerialType] = useState('good');
  const [serialDialogOpen, setSerialDialogOpen] = useState(false);
  const [bulkSerialsInput, setBulkSerialsInput] = useState('');
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [selectedIssueType, setSelectedIssueType] = useState('hardware_issue');
  const [selectedIssueDescription, setSelectedIssueDescription] = useState('');
  const [selectedIssueSeverity, setSelectedIssueSeverity] = useState('medium');

  const [formData, setFormData] = useState({
    name: '',
    product_code: '',
    barcode: '',
    description: '',
    category: '',
    supplier: '',
    location: '',
    price: '',
    cost_price: '',
    quantity: '0',
    good_quantity: '0',
    issue_quantity: '0',
    min_quantity: '0',
    reorder_level: '10',
    unit: 'Piece',
    tax_rate: '0',
    is_active: true,
    has_warranty: false,
    warranty_months: '0',
    warranty_type: 'none',
    warranty_terms: '',
    track_serials: false
  });

  const [profitMargin, setProfitMargin] = useState(0);
  const [totalValue, setTotalValue] = useState(0);
  const [newLocation, setNewLocation] = useState('');
  const [recentLocations, setRecentLocations] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);

  const categories = [
  // ========== LIGHTING ==========
  'LED Bulbs',
  'Tube Lights (LED/CFL)',
  'Smart Bulbs & Lights',
  'Decorative Lights (String, Festoon)',
  'Chandeliers & Pendant Lights',
  'Ceiling Lights (Panel, Surface)',
  'Wall Lights & Sconces',
  'Floor Lamps',
  'Table Lamps',
  'Batten Holders',
  'Emergency Lights & Inverters',
  'Street Lights',
  'Flood Lights',
  'Solar Lights',
  'Light Accessories (Sockets, Holders)',

  // ========== WIRING & ELECTRICAL ACCESSORIES ==========
  'Electrical Wires & Cables',
  'Wire Connectors & Terminals',
  'Switches (1-Way, 2-Way, Dimmer)',
  'Sockets & Outlets',
  'Extension Cords',
  'Power Strips & Multi-Plugs',
  'Electrical Tape',
  'Circuit Breakers',
  'Fuses & Fuse Holders',
  'Distribution Boards',
  'Conduits & Trunking',
  'Wall Plates & Boxes',

  // ========== HOME APPLIANCES ==========
  'Refrigerators',
  'Freezers',
  'Washing Machines',
  'Dryers',
  'Air Conditioners (Split, Window)',
  'Air Coolers',
  'Fans (Ceiling, Wall, Table, Pedestal)',
  'Exhaust Fans',
  'Water Heaters (Geysers)',
  'Water Dispensers',
  'Microwave Ovens',
  'Ovens & Cookers',
  'Induction Cooktops',
  'Electric Kettles',
  'Electric Irons',
  'Vacuum Cleaners',
  'Air Purifiers',
  'Dehumidifiers',
  'Humidifiers',
  'Blenders & Mixers',
  'Juicers',
  'Food Processors',
  'Rice Cookers',
  'Toasters & Sandwich Makers',
  'Coffee Machines',
  'Electric Stoves',

  // ========== KITCHEN ELECTRICAL ==========
  'Chimneys & Hoods',
  'Dishwashers',
  'Water Filters & Purifiers',
  'Refrigerator Accessories',
  'Kitchen Scales',
  'Warmers & Food Display',

  // ========== TOOLS & HARDWARE ==========
  'Power Tools (Drills, Saws, Grinders)',
  'Cordless Tools',
  'Measuring Tools (Multimeters, Testers)',
  'Soldering Stations & Irons',
  'Heat Guns',
  'Tool Kits',
  'Drill Bits & Accessories',
  'Electrical Testers',
  'Clamp Meters',
  'Voltage Stabilizers',

  // ========== AUDIO & ENTERTAINMENT ==========
  'Home Theater Systems',
  'Soundbars',
  'Speakers (Bluetooth, Portable)',
  'Amplifiers & Receivers',
  'Headphones & Earphones',
  'Music Systems',
  'Microphones',
  'Karaoke Systems',

  // ========== VIDEO & PHOTOGRAPHY ==========
  'Televisions (LED, OLED, Smart)',
  'TV Mounts & Stands',
  'Projectors',
  'Projector Screens',
  'Cameras (DSLR, Mirrorless)',
  'CCTV Cameras & Security Systems',
  'Video Doorbells',
  'Action Cameras',
  'Camera Accessories (Tripods, Lenses)',
  'Drones',

  // ========== SMART HOME & AUTOMATION ==========
  'Smart Plugs',
  'Smart Switches',
  'Smart Sensors (Motion, Door, Temp)',
  'Smart Hubs & Controllers',
  'Smart Door Locks',
  'Smart Thermostats',
  'Video Doorbells',
  'Security Cameras',
  'Home Automation Kits',

  // ========== BATTERIES & POWER ==========
  'Batteries (AA, AAA, Rechargeable)',
  'Inverter Batteries',
  'Solar Panels',
  'Solar Charge Controllers',
  'Power Banks',
  'Battery Chargers',
  'UPS (Uninterruptible Power Supply)',
  'Generators & Inverters',

  // ========== ELECTRICAL SAFETY ==========
  'Circuit Breakers',
  'RCCB / ELCB',
  'Surge Protectors',
  'Fire Extinguishers',
  'Safety Gloves & Gear',
  'Insulated Tools',

  // ========== WIRING & INSTALLATION ==========
  'Electrical Conduit Pipes',
  'Cable Trays',
  'Junction Boxes',
  'Wire Ducts',
  'Crimp Tools & Connectors',
  'PVC Tapes',
  'Heat Shrink Tubing',

  // ========== HARDWARE & FITTINGS ==========
  'Bulb Holders (B22, E27)',
  'Plug Tops (2-Pin, 3-Pin)',
  'Adapters & Converters',
  'Fuse Boxes',
  'Timer Switches',
  'Dimmer Modules',
  'Sensors (Motion, Light, Occupancy)',

  // ========== VENTILATION & COOLING ==========
  'Ceiling Fans',
  'Wall Fans',
  'Exhaust Fans',
  'Industrial Fans',
  'Air Circulators',
  'Ventilation Systems',
  'Cooling Pads',

  // ========== INDUSTRIAL & COMMERCIAL ==========
  'Industrial Motors',
  'Generators',
  'Transformers',
  'Commercial Lighting',
  'Heating Elements',
  'Industrial Switches & Contactors',
  'Control Panels',
  'Energy Meters',

  // ========== AUTOMOTIVE ELECTRICAL ==========
  'Car Batteries',
  'Car Audio Systems',
  'Dash Cams',
  'Car Chargers',
  'Jump Starters',
  'Automotive Bulbs',
  'Car Wiring Accessories',

  // ========== TELECOM & COMMUNICATION ==========
  'Telephone Systems',
  'Intercom Systems',
  'Network Cables',
  'Fiber Optic Cables',
  'Signal Boosters',
  'Antennas',

  // ========== MISCELLANEOUS ==========
  'Extension Boards',
  'Timer Switches',
  'Digital Clocks',
  'Weather Stations',
  'Electric Fencing',
  'Water Pumps',
  'Garage Door Openers',
  'Electric Curtain Motors',
  'Doorbells & Chimes'
];

  // Products that typically have serial numbers
  const serialTrackedCategories = [
    'Desktop Computers', 'Laptop Computers', 'All-in-One Computers', 
    'Workstation PCs', 'Monitors', 'Projectors', 'Printers', 'Scanners',
    'Gaming Consoles', 'VR Headsets', 'Routers', 'Switches'
  ];

  const units = [
    'Piece', 'Kg', 'Gram', 'Liter', 'Pack', 'Box', 'Meter', 'Set', 'Dozen', 'Pair'
  ];

  // Expanded locations list
  const predefinedLocations = [
    'Main Warehouse', 'Store Front', 'Storage Room', 'Display Area',
    'Back Room'
  ];

  const commonWarehouseLocations = [
    'Bulk Storage Area',
    'High Value Storage', 'Fragile Items Area', 'Heavy Items Area'
  ];

  const warrantyTypes = [
    { value: 'none', label: 'No Warranty' },
    { value: 'manufacturer', label: 'Manufacturer Warranty' },
    { value: 'seller', label: 'Seller Warranty' },
    { value: 'extended', label: 'Extended Warranty' }
  ];

  const issueTypes = [
    { value: 'keyboard_issue', label: 'Keyboard Issue', icon: '⌨️' },
    { value: 'motherboard_issue', label: 'Motherboard Issue', icon: '🔌' },
    { value: 'screen_issue', label: 'Screen Issue', icon: '🖥️' },
    { value: 'battery_issue', label: 'Battery Issue', icon: '🔋' },
    { value: 'charging_issue', label: 'Charging Issue', icon: '⚡' },
    { value: 'wifi_issue', label: 'WiFi Issue', icon: '📶' },
    { value: 'speaker_issue', label: 'Speaker Issue', icon: '🔊' },
    { value: 'fan_issue', label: 'Fan Issue', icon: '🌀' },
    { value: 'overheating', label: 'Overheating', icon: '🔥' },
    { value: 'software_issue', label: 'Software Issue', icon: '💻' },
    { value: 'hardware_issue', label: 'Hardware Issue', icon: '🛠️' },
    { value: 'physical_damage', label: 'Physical Damage', icon: '💔' },
    { value: 'other', label: 'Other', icon: '❓' }
  ];

  const severityLevels = [
    { value: 'low', label: 'Low', color: 'success' },
    { value: 'medium', label: 'Medium', color: 'warning' },
    { value: 'high', label: 'High', color: 'error' },
    { value: 'critical', label: 'Critical', color: 'error', variant: 'filled' }
  ];

  const steps = [
    'Basic Information',
    'Pricing & Inventory',
    'Serial Numbers',
    'Additional Details'
  ];

  // Get all locations for suggestions
  const allLocations = [...predefinedLocations, ...commonWarehouseLocations];

  // Get top 8 frequently used locations for quick selection
  const quickLocations = [
    'Main Warehouse', 'Store Front', 'Storage Room', 'Display Area',
    'Aisle 1 - Shelf A', 'Section 1 - Rack 1', 'Back Room', 'Online Stock'
  ];

  // Get issue type icon
  // Get issue type icon
const getIssueTypeIcon = (issueType) => {
  const iconMap = {
    'keyboard_issue': <KeyboardIcon fontSize="small" />,
    'motherboard_issue': <MemoryIcon fontSize="small" />,
    'screen_issue': <CategoryIcon fontSize="small" />,  // This is correct - CategoryIcon is imported as Category
    'battery_issue': <BatteryIcon fontSize="small" />,
    'charging_issue': <BatteryIcon fontSize="small" />,
    'wifi_issue': <WifiIcon fontSize="small" />,
    'speaker_issue': <SpeakerIcon fontSize="small" />,
    'fan_issue': <BuildIcon fontSize="small" />,
    'overheating': <WhatshotIcon fontSize="small" />,
    'software_issue': <BugReportIcon fontSize="small" />,
    'hardware_issue': <BuildIcon fontSize="small" />,
    'physical_damage': <WarningIcon fontSize="small" />,
    'other': <WarningIcon fontSize="small" />
  };
  return iconMap[issueType] || <WarningIcon fontSize="small" />;
};

  // Get issue type label
  const getIssueTypeLabel = (issueType) => {
    const labelMap = {
      'keyboard_issue': 'Keyboard Issue',
      'motherboard_issue': 'Motherboard Issue',
      'screen_issue': 'Screen Issue',
      'battery_issue': 'Battery Issue',
      'charging_issue': 'Charging Issue',
      'wifi_issue': 'WiFi Issue',
      'speaker_issue': 'Speaker Issue',
      'fan_issue': 'Fan Issue',
      'overheating': 'Overheating',
      'software_issue': 'Software Issue',
      'hardware_issue': 'Hardware Issue',
      'physical_damage': 'Physical Damage',
      'other': 'Other Issue'
    };
    return labelMap[issueType] || issueType;
  };

  useEffect(() => {
    if (isEditMode) {
      fetchProduct();
    } else {
      generateProductCode();
      setDataLoaded(true);
    }
    
    // Load recent locations from localStorage
    const savedRecentLocations = localStorage.getItem('recentLocations');
    if (savedRecentLocations) {
      setRecentLocations(JSON.parse(savedRecentLocations));
    }
  }, [id, isEditMode]);

  // Log formData changes for debugging
  useEffect(() => {
    console.log('📊 Current formData:', formData);
  }, [formData]);

  useEffect(() => {
    if (dataLoaded) {
      calculateProfitAndValue();
      if (!trackSerials) {
        // If not tracking serials, good_quantity = total quantity
        setFormData(prev => ({
          ...prev,
          good_quantity: prev.quantity,
          issue_quantity: '0'
        }));
      } else {
        updateQuantitiesFromSerials();
      }
    }
  }, [formData.price, formData.cost_price, formData.quantity, dataLoaded, trackSerials]);

  // Update quantities when serials change (if tracking serials)
  useEffect(() => {
    if (trackSerials) {
      updateQuantitiesFromSerials();
    }
  }, [goodSerials, issueSerials, trackSerials]);

  // Effect to handle mode change
  useEffect(() => {
    if (mode === 'existing') {
      setActiveStep(1);
    } else {
      setActiveStep(0);
    }
  }, [mode]);

  // Effect to suggest serial tracking based on category
  useEffect(() => {
    if (formData.category && serialTrackedCategories.includes(formData.category)) {
      setTrackSerials(true);
    }
  }, [formData.category]);

  const generateProductCode = () => {
    const prefix = 'PROD';
    const year = new Date().getFullYear().toString().slice(-2);
    const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    const newCode = `${prefix}${year}${month}${random}`;
    setFormData(prev => ({ ...prev, product_code: newCode }));
  };

  const generateBarcode = () => {
    const barcode = `BC${Date.now()}${Math.floor(Math.random() * 1000)}`;
    setFormData(prev => ({ ...prev, barcode }));
  };

  const calculateProfitAndValue = () => {
    const price = parseFloat(formData.price) || 0;
    const cost = parseFloat(formData.cost_price) || 0;
    const quantity = parseInt(formData.quantity) || 0;

    if (cost > 0) {
      const margin = ((price - cost) / cost) * 100;
      setProfitMargin(margin.toFixed(2));
    } else {
      setProfitMargin(0);
    }

    setTotalValue(price * quantity);
  };

  // FIXED: Enhanced fetchProduct with better error handling and serial data loading
  const fetchProduct = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Fetching product with ID:', id);
      
      const response = await apiService.getProductById(id);
      console.log('📥 Fetched product response:', response);
      
      if (response && response.success) {
        const product = response.data || response;
        console.log('📦 Product data for edit:', product);
        
        if (!product || Object.keys(product).length === 0) {
          setError('No product data received');
          setLoading(false);
          return;
        }
        
        // Set form data
        const newFormData = {
          name: product.name || '',
          product_code: product.product_code || '',
          barcode: product.barcode || '',
          description: product.description || '',
          category: product.category || '',
          supplier: product.supplier || '',
          location: product.location || '',
          price: product.price ? product.price.toString() : '',
          cost_price: product.cost_price ? product.cost_price.toString() : '',
          quantity: product.quantity ? product.quantity.toString() : '0',
          good_quantity: product.good_quantity ? product.good_quantity.toString() : '0',
          issue_quantity: product.issue_quantity ? product.issue_quantity.toString() : '0',
          min_quantity: product.min_quantity ? product.min_quantity.toString() : '0',
          reorder_level: product.reorder_level ? product.reorder_level.toString() : '10',
          unit: product.unit || 'Piece',
          tax_rate: product.tax_rate ? product.tax_rate.toString() : '0',
          is_active: product.is_active !== false,
          has_warranty: product.has_warranty || false,
          warranty_months: product.warranty_months ? product.warranty_months.toString() : '0',
          warranty_type: product.warranty_type || 'none',
          warranty_terms: product.warranty_terms || '',
          track_serials: product.track_serials || false
        };
        
        console.log('📝 Setting form data:', newFormData);
        setFormData(newFormData);
        
        // Determine if we should track serials based on product data
        const shouldTrackSerials = product.track_serials || 
                                   (product.serials && product.serials.length > 0) ||
                                   (product.good_quantity > 0 || product.issue_quantity > 0);
        setTrackSerials(shouldTrackSerials);
        
        // Load serials if they exist
        if (product.serials && product.serials.length > 0) {
          console.log('📦 Loading serials:', product.serials);
          
          const goodSerialsList = [];
          const issueSerialsList = [];
          
          product.serials.forEach(serial => {
            if (serial.status === 'available' || serial.status === 'good') {
              goodSerialsList.push({
                id: serial.id,
                serial: serial.serial_number,
                status: 'good'
              });
            } else if (serial.status === 'damaged' || serial.status === 'issue') {
              // Check if there's an associated issue
              const issue = serial.issue || {};
              issueSerialsList.push({
                id: serial.id,
                serial: serial.serial_number,
                status: 'issue',
                issue_type: issue.issue_type || 'hardware_issue',
                description: issue.issue_description || 'No description',
                severity: issue.severity || 'medium',
                reported_date: issue.reported_date || new Date().toISOString(),
                reported_by: issue.reported_by || 'System',
                notes: issue.notes || ''
              });
            }
          });
          
          setGoodSerials(goodSerialsList);
          setIssueSerials(issueSerialsList);
        } else {
          // If no serials but we have quantities, create placeholder serials for display
          const goodQty = parseInt(product.good_quantity) || 0;
          const issueQty = parseInt(product.issue_quantity) || 0;
          
          if (goodQty > 0) {
            const placeholderGoodSerials = [];
            for (let i = 1; i <= goodQty; i++) {
              placeholderGoodSerials.push({
                id: `temp-good-${i}`,
                serial: `${product.product_code}-GOOD-${i.toString().padStart(3, '0')}`,
                status: 'good',
                isPlaceholder: true
              });
            }
            setGoodSerials(placeholderGoodSerials);
          }
          
          if (issueQty > 0) {
            const placeholderIssueSerials = [];
            for (let i = 1; i <= issueQty; i++) {
              placeholderIssueSerials.push({
                id: `temp-issue-${i}`,
                serial: `${product.product_code}-ISSUE-${i.toString().padStart(3, '0')}`,
                status: 'issue',
                issue_type: 'hardware_issue',
                description: 'Issue details pending',
                severity: 'medium',
                isPlaceholder: true
              });
            }
            setIssueSerials(placeholderIssueSerials);
          }
        }
        
        setDataLoaded(true);
        
        setTimeout(() => {
          calculateProfitAndValue();
        }, 100);
        
      } else {
        console.error('❌ Invalid response format:', response);
        setError(response?.message || 'Failed to load product data');
      }
    } catch (err) {
      console.error('❌ Error fetching product:', err);
      setError(err.message || 'Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  // Update quantities based on serial numbers
  const updateQuantitiesFromSerials = () => {
    if (trackSerials) {
      const goodCount = goodSerials.length;
      const issueCount = issueSerials.length;
      const totalCount = goodCount + issueCount;
      
      setFormData(prev => ({
        ...prev,
        good_quantity: goodCount.toString(),
        issue_quantity: issueCount.toString(),
        quantity: totalCount.toString()
      }));
    }
  };

  // Handle quantity change (when not tracking serials)
  const handleQuantityChange = (e) => {
    const newQuantity = e.target.value;
    setFormData(prev => ({
      ...prev,
      quantity: newQuantity,
      good_quantity: !trackSerials ? newQuantity : prev.good_quantity,
      issue_quantity: !trackSerials ? '0' : prev.issue_quantity
    }));
  };

  // Handle toggle serial tracking
  const handleToggleSerials = () => {
    if (!trackSerials && (goodSerials.length > 0 || issueSerials.length > 0)) {
      setShowSerialWarning(true);
    } else {
      setTrackSerials(!trackSerials);
      if (!trackSerials) {
        // Turning ON serial tracking
        setGoodSerials([]);
        setIssueSerials([]);
        setFormData(prev => ({
          ...prev,
          good_quantity: '0',
          issue_quantity: '0'
        }));
      } else {
        // Turning OFF serial tracking
        setFormData(prev => ({
          ...prev,
          good_quantity: prev.quantity,
          issue_quantity: '0'
        }));
      }
    }
  };

  const handleSearchExisting = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a search term');
      return;
    }

    try {
      setSearching(true);
      setError(null);
      
      const query = searchQuery.trim();
      console.log('🔍 Searching products for:', query);
      
      const response = await apiService.searchProducts(`?query=${encodeURIComponent(query)}`);
      console.log('📥 Search response:', response);
      
      if (response.success) {
        setSearchResults(response.data || []);
        setShowSearchResults(true);
        setPage(0);
        
        if (response.data.length === 0) {
          setError('No products found. Try a different search term or create a new product.');
        }
      }
    } catch (err) {
      console.error('❌ Search error:', err);
      setError('Failed to search products');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectExistingProduct = (product) => {
    setSelectedExistingProduct(product);
    setShowSearchResults(false);
    setSearchQuery('');
    setAdditionalQuantity('');
    
    setFormData(prev => ({
      ...prev,
      location: product.location || '',
      price: product.price ? product.price.toString() : ''
    }));
  };

  const handleClearSelected = () => {
    setSelectedExistingProduct(null);
    setAdditionalQuantity('');
    setSearchResults([]);
    setFormData(prev => ({
      ...prev,
      location: '',
      price: ''
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLocationChange = (location) => {
    setFormData(prev => ({ ...prev, location }));
    
    if (location && !recentLocations.includes(location)) {
      const updatedRecent = [location, ...recentLocations].slice(0, 10);
      setRecentLocations(updatedRecent);
      localStorage.setItem('recentLocations', JSON.stringify(updatedRecent));
    }
  };

  const handleAddCustomLocation = () => {
    if (newLocation.trim()) {
      handleLocationChange(newLocation.trim());
      setNewLocation('');
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSerialTabChange = (event, newValue) => {
    setSerialTab(newValue);
  };

  // ==================== SERIAL NUMBER FUNCTIONS ====================

  const handleAddGoodSerial = () => {
    setSerialType('good');
    setNewSerial('');
    setEditingSerial(null);
    setSerialDialogOpen(true);
  };

  const handleAddIssueSerial = () => {
    setSerialType('issue');
    setNewSerial('');
    setSelectedIssueType('hardware_issue');
    setSelectedIssueDescription('');
    setSelectedIssueSeverity('medium');
    setEditingSerial(null);
    setSerialDialogOpen(true);
  };

  const handleSaveSerial = () => {
    if (!newSerial.trim()) {
      setError('Serial number is required');
      return;
    }

    const allSerials = [...goodSerials, ...issueSerials];
    if (allSerials.some(s => s.serial === newSerial.trim()) && !editingSerial) {
      setError('Serial number already exists');
      return;
    }

    if (editingSerial) {
      if (serialType === 'good') {
        setGoodSerials(prev => prev.map(s => 
          s.id === editingSerial.id ? { ...s, serial: newSerial.trim() } : s
        ));
      } else {
        setIssueSerials(prev => prev.map(s => 
          s.id === editingSerial.id ? { 
            ...s, 
            serial: newSerial.trim(),
            issue_type: selectedIssueType,
            description: selectedIssueDescription,
            severity: selectedIssueSeverity
          } : s
        ));
      }
    } else {
      const newSerialObj = {
        id: Date.now() + Math.random(),
        serial: newSerial.trim(),
        status: serialType
      };

      if (serialType === 'issue') {
        newSerialObj.issue_type = selectedIssueType;
        newSerialObj.description = selectedIssueDescription;
        newSerialObj.severity = selectedIssueSeverity;
        newSerialObj.reported_date = new Date().toISOString();
        setIssueSerials(prev => [...prev, newSerialObj]);
      } else {
        setGoodSerials(prev => [...prev, newSerialObj]);
      }
    }

    setSerialDialogOpen(false);
    setNewSerial('');
    setSelectedIssueType('hardware_issue');
    setSelectedIssueDescription('');
    setSelectedIssueSeverity('medium');
    setEditingSerial(null);
  };

  const handleEditSerial = (serial, type) => {
    setSerialType(type);
    setNewSerial(serial.serial);
    setEditingSerial(serial);
    
    if (type === 'issue') {
      setSelectedIssueType(serial.issue_type || 'hardware_issue');
      setSelectedIssueDescription(serial.description || '');
      setSelectedIssueSeverity(serial.severity || 'medium');
    }
    
    setSerialDialogOpen(true);
  };

  const handleDeleteSerial = (id, type) => {
    if (window.confirm('Are you sure you want to delete this serial number?')) {
      if (type === 'good') {
        setGoodSerials(prev => prev.filter(s => s.id !== id));
      } else {
        setIssueSerials(prev => prev.filter(s => s.id !== id));
      }
    }
  };

  const handleBulkAdd = () => {
    setBulkDialogOpen(true);
    setBulkSerialsInput('');
  };

  const handleSaveBulk = () => {
    if (!bulkSerialsInput.trim()) {
      setError('Please enter serial numbers');
      return;
    }

    const lines = bulkSerialsInput.split('\n').filter(line => line.trim() !== '');
    const newSerials = [];
    const errors = [];

    lines.forEach((line, index) => {
      const serial = line.trim();
      
      const allSerials = [...goodSerials, ...issueSerials];
      if (allSerials.some(s => s.serial === serial)) {
        errors.push(`Line ${index + 1}: Serial ${serial} already exists`);
      } else {
        newSerials.push({
          id: Date.now() + Math.random() + index,
          serial: serial,
          status: serialTab === 0 ? 'good' : 'issue',
          ...(serialTab === 1 ? {
            issue_type: 'hardware_issue',
            description: 'Bulk added issue',
            severity: 'medium',
            reported_date: new Date().toISOString()
          } : {})
        });
      }
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }

    if (serialTab === 0) {
      setGoodSerials(prev => [...prev, ...newSerials]);
    } else {
      setIssueSerials(prev => [...prev, ...newSerials]);
    }

    setBulkDialogOpen(false);
    setBulkSerialsInput('');
  };

  const handleExportSerials = () => {
    const serials = serialTab === 0 ? goodSerials : issueSerials;
    if (serials.length === 0) {
      alert('No serial numbers to export');
      return;
    }

    let csvContent = 'Serial Number,Status';
    if (serialTab === 1) {
      csvContent += ',Issue Type,Description,Severity,Reported Date';
    }
    csvContent += '\n';

    serials.forEach(s => {
      csvContent += `${s.serial},${s.status}`;
      if (serialTab === 1) {
        csvContent += `,${s.issue_type},${s.description || ''},${s.severity},${s.reported_date ? new Date(s.reported_date).toLocaleDateString() : ''}`;
      }
      csvContent += '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formData.product_code}_${serialTab === 0 ? 'good' : 'issue'}_serials.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const validateStep = (step) => {
    setValidationErrors([]);
    
    if (mode === 'existing') {
      if (!selectedExistingProduct) {
        setError('Please select an existing product first');
        return false;
      }
      if (!additionalQuantity || parseInt(additionalQuantity) <= 0) {
        setError('Please enter a valid quantity to add (must be greater than 0)');
        return false;
      }
      return true;
    }

    const errors = [];
    
    switch (step) {
      case 0:
        if (!formData.name.trim()) {
          errors.push('Product name is required');
        }
        if (!formData.category.trim()) {
          errors.push('Category is required');
        }
        break;
      case 1:
        if (!formData.price || parseFloat(formData.price) <= 0) {
          errors.push('Valid price is required (must be greater than 0)');
        }
        if (formData.cost_price && parseFloat(formData.cost_price) < 0) {
          errors.push('Cost price cannot be negative');
        }
        if (formData.quantity && parseInt(formData.quantity) < 0) {
          errors.push('Quantity cannot be negative');
        }
        break;
      case 2:
        if (trackSerials) {
          const totalSerials = goodSerials.length + issueSerials.length;
          const totalQty = parseInt(formData.quantity) || 0;
          
          if (totalSerials !== totalQty) {
            errors.push(`Serial numbers count (${totalSerials}) must match total quantity (${totalQty})`);
          }
        }
        break;
    }
    
    if (errors.length > 0) {
      setValidationErrors(errors);
      setError(errors.join(', '));
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors([]);
    
    if (mode === 'existing') {
      if (!selectedExistingProduct) {
        setError('Please select an existing product');
        return;
      }
      if (!additionalQuantity || parseInt(additionalQuantity) <= 0) {
        setError('Please enter a valid quantity to add');
        return;
      }
    } else {
      for (let i = 0; i < steps.length; i++) {
        if (!validateStep(i)) {
          setActiveStep(i);
          return;
        }
      }
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      let response;

      if (mode === 'existing' && selectedExistingProduct) {
        const currentQuantity = parseInt(selectedExistingProduct.quantity) || 0;
        const addQuantity = parseInt(additionalQuantity) || 0;
        const newQuantity = currentQuantity + addQuantity;
        
        const updateData = {
          quantity: newQuantity
        };
        
        if (formData.location && formData.location.trim()) {
          updateData.location = formData.location.trim();
        }
        
        if (formData.price && parseFloat(formData.price) > 0 && 
            parseFloat(formData.price) !== parseFloat(selectedExistingProduct.price)) {
          updateData.price = parseFloat(formData.price);
        }

        console.log('📤 Updating existing product:', updateData);
        response = await apiService.updateProduct(selectedExistingProduct.id, updateData);
        
        if (response && response.success) {
          setSuccess(
            <Box>
              <Typography variant="body1">
                ✅ Successfully added <strong>{addQuantity}</strong> units to <strong>{selectedExistingProduct.name}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Previous Stock: {currentQuantity} | New Stock: {newQuantity} {selectedExistingProduct.unit}
              </Typography>
            </Box>
          );
        } else {
          setError(response?.message || 'Failed to update product');
        }
      } else {
        // Prepare serials data
        const serialsData = {
          good: goodSerials.map(s => ({
            serial: s.serial
          })),
          issue: issueSerials.map(s => ({
            serial: s.serial,
            issue_type: s.issue_type || 'hardware_issue',
            description: s.description || 'No description',
            severity: s.severity || 'medium',
            reported_by: s.reported_by || 'System',
            reported_date: s.reported_date || new Date().toISOString(),
            notes: s.notes || ''
          }))
        };

        const dataToSend = {
          name: formData.name || '',
          product_code: formData.product_code || '',
          barcode: formData.barcode || null,
          description: formData.description || null,
          category: formData.category || '',
          supplier: formData.supplier || null,
          location: formData.location || null,
          price: parseFloat(formData.price) || 0,
          cost_price: parseFloat(formData.cost_price) || 0,
          quantity: trackSerials ? (goodSerials.length + issueSerials.length) : (parseInt(formData.quantity) || 0),
          good_quantity: trackSerials ? goodSerials.length : (parseInt(formData.quantity) || 0),
          issue_quantity: trackSerials ? issueSerials.length : 0,
          min_quantity: parseInt(formData.min_quantity) || 0,
          reorder_level: parseInt(formData.reorder_level) || 10,
          unit: formData.unit || 'Piece',
          tax_rate: parseFloat(formData.tax_rate) || 0,
          is_active: formData.is_active,
          has_warranty: formData.has_warranty || false,
          warranty_months: parseInt(formData.warranty_months) || 0,
          warranty_type: formData.warranty_type || 'none',
          warranty_terms: formData.warranty_terms || null,
          track_serials: trackSerials,
          serials: trackSerials ? serialsData : null
        };

        // Remove undefined or null values
        Object.keys(dataToSend).forEach(key => {
          if (dataToSend[key] === undefined || dataToSend[key] === null) {
            delete dataToSend[key];
          }
        });

        console.log('📤 Sending data for product ID:', id);
        console.log('📤 Data being sent:', JSON.stringify(dataToSend, null, 2));

        if (isEditMode) {
          console.log('📤 Updating product with ID:', id);
          response = await apiService.updateProduct(id, dataToSend);
        } else {
          console.log('📤 Creating new product');
          response = await apiService.createProduct(dataToSend);
        }

        if (response && response.success) {
          setSuccess(isEditMode ? '✅ Product updated successfully!' : '✅ Product created successfully!');
        } else {
          if (response && response.errors) {
            setValidationErrors(response.errors);
            setError(response.errors.join(', '));
          } else {
            setError(response?.message || 'Failed to save product');
          }
        }
      }

      if (response && response.success) {
        setTimeout(() => {
          navigate('/products');
        }, 2000);
      }
    } catch (err) {
      console.error('❌ Save product error:', err);
      
      if (err && err.errors) {
        setValidationErrors(err.errors);
        setError(err.errors.join(', '));
      } else {
        setError(err.message || `Failed to ${isEditMode ? 'update' : (mode === 'existing' ? 'add to' : 'create')} product`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/products');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ flexGrow: 1, color: '#2c3e50', fontWeight: 'bold' }}>
            {isEditMode ? '✏️ Edit Product' : (mode === 'existing' ? '📦 Add to Existing Product' : '➕ Add New Product')}
          </Typography>
        </Box>

        {!isEditMode && (
          <Card sx={{ mb: 4, backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
            <CardContent>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Select Operation
                </FormLabel>
                <RadioGroup
                  row
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                >
                  <FormControlLabel 
                    value="new" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <NewProductIcon color="primary" />
                        <Typography>Create New Product</Typography>
                      </Box>
                    } 
                  />
                  <FormControlLabel 
                    value="existing" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddToExistingIcon color="secondary" />
                        <Typography>Add to Existing Product (Increase Stock)</Typography>
                      </Box>
                    } 
                  />
                </RadioGroup>
                <FormHelperText>
                  {mode === 'new' 
                    ? 'Create a brand new product in the system with all details' 
                    : 'Search for an existing product and add more quantity to its stock'}
                </FormHelperText>
              </FormControl>
            </CardContent>
          </Card>
        )}

        {mode === 'existing' && !isEditMode && (
          <Card sx={{ mb: 4, backgroundColor: '#e3f2fd', border: '1px solid #90caf9' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SearchIcon color="primary" /> Search Existing Product
              </Typography>
              
              {!selectedExistingProduct ? (
                <>
                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="Search by Name, Code, or Barcode"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearchExisting()}
                        placeholder="e.g., Laptop, PROD24010001, BC123456"
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        variant="contained"
                        onClick={handleSearchExisting}
                        disabled={searching || !searchQuery.trim()}
                        startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
                        fullWidth
                        size="large"
                        sx={{ height: '56px' }}
                      >
                        {searching ? 'Searching...' : 'Search Products'}
                      </Button>
                    </Grid>
                  </Grid>

                  {showSearchResults && searchResults.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Search Results ({searchResults.length} products found)
                      </Typography>
                      
                      <TableContainer component={TablePaper} sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Product</TableCell>
                              <TableCell>Code</TableCell>
                              <TableCell>Category</TableCell>
                              <TableCell align="right">Price</TableCell>
                              <TableCell align="right">Stock</TableCell>
                              <TableCell>Location</TableCell>
                              <TableCell align="center">Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {searchResults
                              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                              .map((product) => (
                                <TableRow 
                                  key={product.id}
                                  hover
                                  sx={{ 
                                    cursor: 'pointer'
                                  }}
                                >
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="bold">
                                      {product.name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      size="small" 
                                      label={product.product_code} 
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell>{product.category}</TableCell>
                                  <TableCell align="right">{formatCurrency(product.price)}</TableCell>
                                  <TableCell align="right">
                                    <Chip 
                                      size="small" 
                                      label={product.quantity}
                                      color={product.quantity <= (product.reorder_level || 10) ? 'warning' : 'success'}
                                      sx={{ minWidth: 60 }}
                                    />
                                  </TableCell>
                                  <TableCell>{product.location || 'N/A'}</TableCell>
                                  <TableCell align="center">
                                    <Button
                                      variant="contained"
                                      size="small"
                                      color="primary"
                                      onClick={() => handleSelectExistingProduct(product)}
                                      startIcon={<CheckIcon />}
                                    >
                                      Select
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={searchResults.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Box>
                  <Alert 
                    severity="success" 
                    sx={{ mb: 2 }}
                    action={
                      <Button color="inherit" size="small" onClick={handleClearSelected}>
                        Change
                      </Button>
                    }
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      Selected Product: {selectedExistingProduct.name}
                    </Typography>
                    <Typography variant="body2">
                      Code: {selectedExistingProduct.product_code} | 
                      Current Stock: {selectedExistingProduct.quantity} {selectedExistingProduct.unit} | 
                      Price: {formatCurrency(selectedExistingProduct.price)}
                    </Typography>
                  </Alert>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Quantity to Add *"
                        type="number"
                        value={additionalQuantity}
                        onChange={(e) => setAdditionalQuantity(e.target.value)}
                        InputProps={{ 
                          inputProps: { min: 1, step: 1 },
                          endAdornment: (
                            <InputAdornment position="end">
                              {selectedExistingProduct.unit}
                            </InputAdornment>
                          )
                        }}
                        helperText="Enter the number of units to add to current stock"
                        required
                        variant="outlined"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined" sx={{ p: 2, backgroundColor: '#f5f5f5', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">Stock Summary</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Current:</Typography>
                            <Typography variant="h6">{selectedExistingProduct.quantity}</Typography>
                          </Box>
                          <Typography variant="h5" color="primary">+</Typography>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Adding:</Typography>
                            <Typography variant="h6">{additionalQuantity || '0'}</Typography>
                          </Box>
                          <Typography variant="h5" color="primary">=</Typography>
                          <Box>
                            <Typography variant="caption" color="text.secondary">New Total:</Typography>
                            <Typography variant="h6" color="success.main" fontWeight="bold">
                              {parseInt(selectedExistingProduct.quantity) + (parseInt(additionalQuantity) || 0)}
                            </Typography>
                          </Box>
                        </Box>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Location (Optional)"
                        value={formData.location}
                        onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                        placeholder={selectedExistingProduct.location || "Enter new location"}
                        helperText="Update storage location if changed"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Price (Optional)"
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                        placeholder={selectedExistingProduct.price}
                        InputProps={{ 
                          startAdornment: <InputAdornment position="start">Rs.</InputAdornment>,
                          inputProps: { min: 0, step: 0.01 }
                        }}
                        helperText="Update selling price if changed"
                      />
                    </Grid>

                    {formData.price && parseFloat(formData.price) !== parseFloat(selectedExistingProduct.price) && (
                      <Grid item xs={12}>
                        <Alert severity="info" icon={<WarningIcon />}>
                          Price will be updated from {formatCurrency(selectedExistingProduct.price)} to {formatCurrency(parseFloat(formData.price))}
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {(mode === 'new' || isEditMode) && (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Stepper activeStep={activeStep} orientation="horizontal" sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* Step 0: Basic Information */}
              {activeStep === 0 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalOfferIcon /> Basic Information
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="Product Name *"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        margin="normal"
                        helperText="Enter the full product name"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Product Code *"
                        name="product_code"
                        value={formData.product_code}
                        onChange={handleChange}
                        required
                        margin="normal"
                        InputProps={{
                          endAdornment: !isEditMode && (
                            <IconButton size="small" onClick={generateProductCode}>
                              <Tooltip title="Generate Code">
                                <RefreshIcon fontSize="small" />
                              </Tooltip>
                            </IconButton>
                          )
                        }}
                        helperText="Unique identifier for the product"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Barcode / SKU"
                        name="barcode"
                        value={formData.barcode}
                        onChange={handleChange}
                        margin="normal"
                        InputProps={{
                          endAdornment: !isEditMode && (
                            <IconButton size="small" onClick={generateBarcode}>
                              <Tooltip title="Generate Barcode">
                                <BarcodeIcon fontSize="small" />
                              </Tooltip>
                            </IconButton>
                          )
                        }}
                        helperText="Leave blank to auto-generate"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        freeSolo
                        options={categories}
                        value={formData.category}
                        onChange={(event, newValue) => {
                          setFormData(prev => ({ ...prev, category: newValue || '' }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Category *"
                            margin="normal"
                            fullWidth
                            required
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        margin="normal"
                        multiline
                        rows={3}
                        helperText="Detailed product description"
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Step 1: Pricing & Inventory */}
              {activeStep === 1 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachMoneyIcon /> Pricing & Inventory
                    </Typography>
                    
                    <Card sx={{ p: 1, bgcolor: '#f5f5f5' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={trackSerials}
                            onChange={handleToggleSerials}
                            color="primary"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {trackSerials ? <ToggleOnIcon color="primary" /> : <ToggleOffIcon color="disabled" />}
                            <Typography variant="body2">
                              {trackSerials ? 'Tracking Serial Numbers' : 'Not Tracking Serials'}
                            </Typography>
                          </Box>
                        }
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 5 }}>
                        {trackSerials 
                          ? 'Products like laptops, monitors with unique serial numbers' 
                          : 'Products like keyboards, mice without serial numbers'}
                      </Typography>
                    </Card>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Selling Price (LKR) *"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        type="number"
                        InputProps={{ 
                          inputProps: { min: 0, step: 0.01 },
                          startAdornment: <InputAdornment position="start">Rs.</InputAdornment>
                        }}
                        required
                        margin="normal"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Cost Price (LKR)"
                        name="cost_price"
                        value={formData.cost_price}
                        onChange={handleChange}
                        type="number"
                        InputProps={{ 
                          inputProps: { min: 0, step: 0.01 },
                          startAdornment: <InputAdornment position="start">Rs.</InputAdornment>
                        }}
                        margin="normal"
                        helperText="For profit calculation"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Tax Rate (%)"
                        name="tax_rate"
                        value={formData.tax_rate}
                        onChange={handleChange}
                        type="number"
                        InputProps={{ inputProps: { min: 0, max: 100, step: 0.01 } }}
                        margin="normal"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Unit</InputLabel>
                        <Select
                          name="unit"
                          value={formData.unit}
                          onChange={handleChange}
                          label="Unit"
                        >
                          {units.map(unit => (
                            <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Total Stock Quantity *"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleQuantityChange}
                        type="number"
                        InputProps={{ 
                          inputProps: { min: 0, step: 1 },
                          readOnly: trackSerials,
                          sx: trackSerials ? { bgcolor: '#f5f5f5' } : {}
                        }}
                        margin="normal"
                        helperText={trackSerials ? "Auto-calculated from serials" : "Total items in stock"}
                        required
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Good Stock"
                        value={trackSerials ? goodSerials.length : formData.quantity}
                        disabled
                        margin="normal"
                        InputProps={{
                          readOnly: true,
                          sx: { color: trackSerials ? '#2e7d32' : 'inherit', fontWeight: 'bold' }
                        }}
                        helperText={trackSerials ? "From good serials" : "All stock is good"}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Issue Stock"
                        value={trackSerials ? issueSerials.length : '0'}
                        disabled
                        margin="normal"
                        InputProps={{
                          readOnly: true,
                          sx: { color: issueSerials.length > 0 ? '#c62828' : 'inherit', fontWeight: 'bold' }
                        }}
                        helperText={trackSerials ? "From issue serials" : "No issues tracked"}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Reorder Level"
                        name="reorder_level"
                        value={formData.reorder_level}
                        onChange={handleChange}
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        margin="normal"
                        helperText="Low stock alert"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Minimum Quantity"
                        name="min_quantity"
                        value={formData.min_quantity}
                        onChange={handleChange}
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        margin="normal"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Available for Sale"
                        value={trackSerials ? goodSerials.length : formData.quantity}
                        disabled
                        margin="normal"
                        InputProps={{
                          readOnly: true,
                          sx: { color: '#2e7d32', fontWeight: 'bold' }
                        }}
                        helperText="Items available for sale"
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ mt: 2, backgroundColor: '#f8f9fa' }}>
                        <CardContent>
                          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalculateIcon fontSize="small" /> Calculations
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Profit Margin:
                              </Typography>
                              <Typography variant="body1" color={profitMargin > 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                                {profitMargin}%
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Total Value:
                              </Typography>
                              <Typography variant="body1" color="primary" fontWeight="bold">
                                {formatCurrency(totalValue)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Step 2: Serial Numbers - Only shown if tracking serials */}
              {activeStep === 2 && trackSerials && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SerialIcon /> Serial Numbers Management
                    </Typography>
                    <Chip 
                      label={`Total: ${goodSerials.length + issueSerials.length} serials`}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={serialTab} onChange={handleSerialTabChange}>
                      <Tab 
                        icon={<GoodIcon />} 
                        label={
                          <Badge badgeContent={goodSerials.length} color="success" sx={{ ml: 1 }}>
                            Good Stock
                          </Badge>
                        } 
                      />
                      <Tab 
                        icon={<ErrorIcon />} 
                        label={
                          <Badge badgeContent={issueSerials.length} color="error" sx={{ ml: 1 }}>
                            Issue Stock
                          </Badge>
                        } 
                      />
                    </Tabs>
                  </Box>

                  {/* Good Stock Tab */}
                  {serialTab === 0 && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1">
                          Good Stock Serial Numbers ({goodSerials.length})
                        </Typography>
                        <Box>
                          <Tooltip title="Export Serials">
                            <IconButton onClick={handleExportSerials} color="primary">
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Bulk Add">
                            <IconButton onClick={handleBulkAdd} color="secondary">
                              <BulkAddIcon />
                            </IconButton>
                          </Tooltip>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={handleAddGoodSerial}
                            sx={{ ml: 1 }}
                          >
                            Add Serial
                          </Button>
                        </Box>
                      </Box>

                      {goodSerials.length === 0 ? (
                        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                          <SerialIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                          <Typography color="text.secondary">
                            No good stock serial numbers added yet.
                          </Typography>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddGoodSerial}
                            sx={{ mt: 2 }}
                          >
                            Add First Serial
                          </Button>
                        </Paper>
                      ) : (
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Serial Number</TableCell>
                                <TableCell align="center">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {goodSerials.map((serial, index) => (
                                <TableRow key={serial.id} hover>
                                  <TableCell>{index + 1}</TableCell>
                                  <TableCell>
                                    <Chip
                                      icon={<SerialIcon />}
                                      label={serial.serial}
                                      color="success"
                                      variant={serial.isPlaceholder ? "outlined" : "filled"}
                                      size="small"
                                    />
                                    {serial.isPlaceholder && (
                                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        (placeholder)
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditSerial(serial, 'good')}
                                      color="primary"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteSerial(serial.id, 'good')}
                                      color="error"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  )}

                  {/* Issue Stock Tab */}
                  {serialTab === 1 && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1">
                          Issue Stock Serial Numbers ({issueSerials.length})
                        </Typography>
                        <Box>
                          <Tooltip title="Export Serials">
                            <IconButton onClick={handleExportSerials} color="primary">
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Bulk Add">
                            <IconButton onClick={handleBulkAdd} color="secondary">
                              <BulkAddIcon />
                            </IconButton>
                          </Tooltip>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={handleAddIssueSerial}
                            sx={{ ml: 1 }}
                            color="warning"
                          >
                            Add Issue Serial
                          </Button>
                        </Box>
                      </Box>

                      {issueSerials.length === 0 ? (
                        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                          <ErrorIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                          <Typography color="text.secondary">
                            No issue stock serial numbers added yet.
                          </Typography>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddIssueSerial}
                            sx={{ mt: 2 }}
                            color="warning"
                          >
                            Add First Issue
                          </Button>
                        </Paper>
                      ) : (
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Serial Number</TableCell>
                                <TableCell>Issue Type</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Severity</TableCell>
                                <TableCell align="center">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {issueSerials.map((serial, index) => {
                                const issueType = issueTypes.find(t => t.value === serial.issue_type) || issueTypes[11];
                                const severity = severityLevels.find(s => s.value === serial.severity) || severityLevels[1];
                                return (
                                  <TableRow key={serial.id} hover>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                      <Chip
                                        icon={<ErrorIcon />}
                                        label={serial.serial}
                                        color="warning"
                                        variant={serial.isPlaceholder ? "outlined" : "filled"}
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {getIssueTypeIcon(serial.issue_type)}
                                        <Typography variant="body2">
                                          {issueType.label}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip title={serial.description || ''}>
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                          {serial.description || '-'}
                                        </Typography>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={severity.label}
                                        size="small"
                                        color={severity.color}
                                        variant={severity.variant || 'outlined'}
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEditSerial(serial, 'issue')}
                                        color="primary"
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteSerial(serial.id, 'issue')}
                                        color="error"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  )}

                  <Alert severity={goodSerials.length + issueSerials.length === parseInt(formData.quantity) ? "success" : "warning"} sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Summary:</strong> {goodSerials.length} good + {issueSerials.length} issue = {goodSerials.length + issueSerials.length} total
                      {parseInt(formData.quantity) !== (goodSerials.length + issueSerials.length) && (
                        <span style={{ color: '#f57c00', fontWeight: 'bold', display: 'block' }}>
                          ⚠️ Quantity mismatch: Need {parseInt(formData.quantity) - (goodSerials.length + issueSerials.length)} more serial numbers
                        </span>
                      )}
                      {parseInt(formData.quantity) === (goodSerials.length + issueSerials.length) && (
                        <span style={{ color: '#2e7d32', fontWeight: 'bold', display: 'block' }}>
                          ✅ Serial numbers match total quantity
                        </span>
                      )}
                    </Typography>
                  </Alert>
                </>
              )}

              {/* Step 2 Alternative - When not tracking serials */}
              {activeStep === 2 && !trackSerials && (
                <>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InventoryIcon /> Stock Summary
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                    <SettingsIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Serial Number Tracking is Disabled
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      This product is configured without serial number tracking. 
                      All {formData.quantity} units are considered good stock and available for sale.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      If this product should have serial numbers (like laptops, monitors), 
                      go back to the previous step and enable "Track Serial Numbers".
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setActiveStep(1)}
                      sx={{ mt: 2 }}
                    >
                      Back to Pricing & Inventory
                    </Button>
                  </Paper>
                </>
              )}

              {/* Step 3: Additional Details */}
              {activeStep === 3 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon /> Additional Details
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Supplier"
                        name="supplier"
                        value={formData.supplier}
                        onChange={handleChange}
                        margin="normal"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SupplierIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        helperText="Enter supplier name or company"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon fontSize="small" /> Storage Location
                        </Typography>
                        
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Enter Location"
                            value={formData.location}
                            onChange={(e) => handleLocationChange(e.target.value)}
                            placeholder="e.g., Aisle 2 - Shelf B"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Quick Select:
                          </Typography>
                          <Grid container spacing={1}>
                            {quickLocations.map((location) => (
                              <Grid item xs={6} key={location}>
                                <Button
                                  variant={formData.location === location ? "contained" : "outlined"}
                                  size="small"
                                  onClick={() => handleLocationChange(location)}
                                  startIcon={formData.location === location ? <CheckIcon /> : <LocationIcon />}
                                  sx={{ 
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    width: '100%',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  {location}
                                </Button>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>

                        <Button
                          startIcon={showAllLocations ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          onClick={() => setShowAllLocations(!showAllLocations)}
                          size="small"
                          sx={{ textTransform: 'none', mb: 1 }}
                        >
                          {showAllLocations ? 'Hide All Locations' : 'Show All Locations'}
                        </Button>
                        
                        <Collapse in={showAllLocations}>
                          <Paper sx={{ mt: 1, p: 2, maxHeight: 200, overflow: 'auto' }}>
                            <Grid container spacing={1}>
                              {allLocations.map((location) => (
                                <Grid item xs={12} sm={6} key={location}>
                                  <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => handleLocationChange(location)}
                                    startIcon={<LocationIcon fontSize="small" />}
                                    sx={{ 
                                      justifyContent: 'flex-start',
                                      textTransform: 'none',
                                      width: '100%',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    {location}
                                  </Button>
                                </Grid>
                              ))}
                            </Grid>
                          </Paper>
                        </Collapse>

                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                          <TextField
                            size="small"
                            placeholder="Add custom location..."
                            value={newLocation}
                            onChange={(e) => setNewLocation(e.target.value)}
                            fullWidth
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleAddCustomLocation();
                              }
                            }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleAddCustomLocation}
                            disabled={!newLocation.trim()}
                          >
                            Add
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WarrantyIcon fontSize="small" /> Warranty Information
                        </Typography>
                        
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.has_warranty}
                              onChange={(e) => {
                                setFormData(prev => ({
                                  ...prev,
                                  has_warranty: e.target.checked,
                                  warranty_type: e.target.checked ? 'manufacturer' : 'none'
                                }));
                              }}
                            />
                          }
                          label="This product has warranty"
                          sx={{ mb: 2 }}
                        />
                        
                        {formData.has_warranty && (
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Warranty Type</InputLabel>
                                <Select
                                  name="warranty_type"
                                  value={formData.warranty_type}
                                  onChange={handleChange}
                                  label="Warranty Type"
                                >
                                  {warrantyTypes.filter(type => type.value !== 'none').map(type => (
                                    <MenuItem key={type.value} value={type.value}>
                                      {type.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Warranty Period (Months)"
                                name="warranty_months"
                                value={formData.warranty_months}
                                onChange={handleChange}
                                type="number"
                                InputProps={{ inputProps: { min: 0 } }}
                              />
                            </Grid>
                            
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Warranty Terms"
                                name="warranty_terms"
                                value={formData.warranty_terms}
                                onChange={handleChange}
                                multiline
                                rows={2}
                                placeholder="e.g., Warranty covers manufacturing defects only..."
                              />
                            </Grid>
                          </Grid>
                        )}
                      </Card>
                    </Grid>

                    {/* Stock Status Summary */}
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ p: 2, mt: 2, backgroundColor: '#fff3e0', border: '1px solid #ffb74d' }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#ed6c02' }}>
                          <InventoryIcon fontSize="small" /> Stock Status Summary
                        </Typography>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              label="Total Stock"
                              value={formData.quantity}
                              disabled
                              size="small"
                              InputProps={{
                                readOnly: true,
                                sx: { fontWeight: 'bold' }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              label="Good Stock"
                              value={trackSerials ? goodSerials.length : formData.quantity}
                              disabled
                              size="small"
                              InputProps={{
                                readOnly: true,
                                sx: { color: '#2e7d32', fontWeight: 'bold' }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              label="Issue Stock"
                              value={trackSerials ? issueSerials.length : '0'}
                              disabled
                              size="small"
                              InputProps={{
                                readOnly: true,
                                sx: { color: issueSerials.length > 0 ? '#c62828' : 'inherit', fontWeight: 'bold' }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              label="Available for Sale"
                              value={trackSerials ? goodSerials.length : formData.quantity}
                              disabled
                              size="small"
                              InputProps={{
                                readOnly: true,
                                sx: { color: '#2e7d32', fontWeight: 'bold' }
                              }}
                            />
                          </Grid>
                        </Grid>
                        
                        {trackSerials && issueSerials.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <WarningIcon fontSize="small" />
                              This product has {issueSerials.length} item(s) with reported issues.
                            </Typography>
                          </Box>
                        )}
                        
                        {!trackSerials && parseInt(formData.quantity) > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckIcon fontSize="small" />
                              All {formData.quantity} units are good stock and available for sale.
                            </Typography>
                          </Box>
                        )}
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <InventoryIcon fontSize="small" /> Product Status
                        </Typography>
                        
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.is_active}
                              onChange={(e) => {
                                setFormData(prev => ({ ...prev, is_active: e.target.checked }));
                              }}
                            />
                          }
                          label="Product is active and available for sale"
                        />
                      </Card>
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Stepper Navigation */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="outlined"
                >
                  Back
                </Button>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {}}
                  >
                    Finish
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {validationErrors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setValidationErrors([])}>
            <Typography variant="body2" fontWeight="bold">Validation Errors:</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.map((err, index) => (
                <li key={index}>{err}</li>
              ))}
            </ul>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'flex-end', 
            pt: 3, 
            borderTop: 2, 
            borderColor: 'divider'
          }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={saving}
              size="large"
              sx={{ minWidth: 120 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={
                saving || 
                (mode === 'existing' && !selectedExistingProduct) ||
                (mode === 'existing' && !additionalQuantity)
              }
              sx={{
                backgroundColor: mode === 'existing' ? '#27ae60' : '#3498db',
                '&:hover': { 
                  backgroundColor: mode === 'existing' ? '#219653' : '#2980b9' 
                },
                minWidth: 200
              }}
              size="large"
            >
              {saving ? 'Saving...' : (
                mode === 'existing' ? 'Add to Stock' : (isEditMode ? 'Update Product' : 'Create Product')
              )}
            </Button>
          </Box>
        </form>
      </Paper>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon /> Product Summary
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Product Code</Typography>
            <Typography variant="body1" fontWeight="medium">
              {selectedExistingProduct?.product_code || formData.product_code || 'Not set'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Name</Typography>
            <Typography variant="body1" fontWeight="medium">
              {selectedExistingProduct?.name || formData.name || 'Not set'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Category</Typography>
            <Typography variant="body1">
              {selectedExistingProduct?.category || formData.category || 'Not set'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Price</Typography>
            <Typography variant="body1" color="primary" fontWeight="bold">
              {selectedExistingProduct?.price ? formatCurrency(selectedExistingProduct.price) : 
               formData.price ? formatCurrency(parseFloat(formData.price)) : 'Not set'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Total Stock</Typography>
            <Typography variant="body1">
              {selectedExistingProduct ? (
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{selectedExistingProduct.quantity}</span>
                  {additionalQuantity && (
                    <>
                      <span>+</span>
                      <Chip 
                        size="small" 
                        label={additionalQuantity} 
                        color="success" 
                        variant="outlined"
                      />
                      <span>=</span>
                      <strong>{parseInt(selectedExistingProduct.quantity) + (parseInt(additionalQuantity) || 0)}</strong>
                    </>
                  )}
                  <span>{selectedExistingProduct.unit}</span>
                </Box>
              ) : (
                `${formData.quantity} ${formData.unit}`
              )}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Good Stock</Typography>
            <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
              {selectedExistingProduct?.good_quantity || 
               (trackSerials ? goodSerials.length : formData.quantity) || 
               formData.good_quantity || '0'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Issue Stock</Typography>
            <Typography variant="body1" sx={{ color: '#c62828', fontWeight: 'bold' }}>
              {selectedExistingProduct?.issue_quantity || 
               (trackSerials ? issueSerials.length : '0') || 
               formData.issue_quantity || '0'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Location</Typography>
            <Typography variant="body1">
              {formData.location || selectedExistingProduct?.location || 'Not specified'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Serial Tracking</Typography>
            <Chip 
              size="small"
              label={trackSerials ? 'Enabled' : 'Disabled'}
              color={trackSerials ? 'primary' : 'default'}
              icon={trackSerials ? <SerialIcon /> : <SettingsIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <Chip 
              size="small"
              label={formData.is_active ? 'Active' : 'Inactive'}
              color={formData.is_active ? 'success' : 'default'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Total Value</Typography>
            <Typography variant="body1" color="primary" fontWeight="bold">
              {selectedExistingProduct ? 
                formatCurrency(
                  (parseInt(selectedExistingProduct.quantity) + (parseInt(additionalQuantity) || 0)) * 
                  (parseFloat(formData.price) || parseFloat(selectedExistingProduct.price) || 0)
                ) :
                formatCurrency(totalValue)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Single Serial Dialog */}
      <Dialog open={serialDialogOpen} onClose={() => setSerialDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSerial ? 'Edit' : 'Add'} {serialType === 'good' ? 'Good Stock' : 'Issue'} Serial Number
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Serial Number"
                value={newSerial}
                onChange={(e) => setNewSerial(e.target.value)}
                placeholder="Enter serial number"
                required
                autoFocus
              />
            </Grid>
            
            {serialType === 'issue' && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Issue Type</InputLabel>
                    <Select
                      value={selectedIssueType}
                      onChange={(e) => setSelectedIssueType(e.target.value)}
                      label="Issue Type"
                    >
                      {issueTypes.map(type => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Issue Description"
                    multiline
                    rows={2}
                    value={selectedIssueDescription}
                    onChange={(e) => setSelectedIssueDescription(e.target.value)}
                    placeholder="Describe the issue in detail..."
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Severity</InputLabel>
                    <Select
                      value={selectedIssueSeverity}
                      onChange={(e) => setSelectedIssueSeverity(e.target.value)}
                      label="Severity"
                    >
                      {severityLevels.map(severity => (
                        <MenuItem key={severity.value} value={severity.value}>
                          {severity.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSerialDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveSerial}
            variant="contained"
            disabled={!newSerial.trim()}
          >
            {editingSerial ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Add Dialog */}
      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Bulk Add {serialTab === 0 ? 'Good Stock' : 'Issue'} Serial Numbers
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter one serial number per line. Duplicate serial numbers will be rejected.
          </Typography>
          <Typography variant="body2" color="primary" sx={{ mb: 2 }}>
            Total needed: {parseInt(formData.quantity) - (serialTab === 0 ? goodSerials.length : issueSerials.length)} more serials
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={10}
            value={bulkSerialsInput}
            onChange={(e) => setBulkSerialsInput(e.target.value)}
            placeholder="SN00123456&#10;SN00123457&#10;SN00123458"
            variant="outlined"
          />
          {serialTab === 1 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              For bulk import of issue serials, all will be set to default issue type "Hardware Issue" with "Medium" severity.
              You can edit individual issues after adding.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveBulk}
            variant="contained"
            disabled={!bulkSerialsInput.trim()}
          >
            Add All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Warning Dialog for Toggling Off Serials */}
      <Dialog open={showSerialWarning} onClose={() => setShowSerialWarning(false)}>
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have already added serial numbers. Turning off serial tracking will delete all entered serial numbers. 
            Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSerialWarning(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              setTrackSerials(false);
              setGoodSerials([]);
              setIssueSerials([]);
              setFormData(prev => ({
                ...prev,
                good_quantity: prev.quantity,
                issue_quantity: '0'
              }));
              setShowSerialWarning(false);
            }} 
            color="warning"
          >
            Yes, Turn Off
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductForm;